// ─── Theme Zustand store ───────────────────────────────────────────────────────
// Persists the user's theme preference (light / dark / system) and exposes
// the resolved theme that should currently be applied.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeStore {
  preference: ThemePreference;
  setPreference: (next: ThemePreference) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      preference: 'system',
      setPreference: (next) => set({ preference: next }),
      toggle: () => {
        const current = get().preference;
        const resolved = resolveTheme(current);
        set({ preference: resolved === 'dark' ? 'light' : 'dark' });
      },
    }),
    {
      name: 'ucarz-theme',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export const getSystemTheme = (): ResolvedTheme =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

export const resolveTheme = (pref: ThemePreference): ResolvedTheme =>
  pref === 'system' ? getSystemTheme() : pref;
